import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { Checkbox } from '@pesto/ui/components/Checkbox';
import { CurrencyField } from '@pesto/ui/components/Forms/CurrencyField';
import { useMemo } from 'react';
import { z } from 'zod';

import type { LoanModalPayload, ModalProps } from '../types/modal.types';

import { useProcessStatementDueDateMutation } from '../../__generated__/graphql/api';
import { getLoanRefetchQuery } from '../../components/LoanDetails/getLoanRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';
import { mapToOptionalBoolean, YES_NO_OPTIONS } from '../utils/form-helpers';

const schema = z.object({
  addInterest: z.string().optional(),
  addLateFee: z.string().optional(),
  markDelinquent: z.string().optional(),
  cureDelinquency: z.string().optional(),
  lateFeeAmount: z.number().optional(),
  lateFeeGracePeriodDays: z.number().optional(),
  batchSize: z.number().optional(),
  dryRun: z.string().optional(),
  additionalOptions: z.boolean().optional(),
});

export const ProcessStatementDueDateComponent = ({ payload, api }: ModalProps<LoanModalPayload>) => {
  const { loanId } = payload;
  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getLoanRefetchQuery(loanId);
  const [processStatementDueDate, { loading }] = useProcessStatementDueDateMutation(refetchQuery);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        processStatementDueDate({
          variables: {
            loanId,
            dueDateOptions: {
              addInterest: mapToOptionalBoolean(data.addInterest),
              addLateFee: mapToOptionalBoolean(data.addLateFee),
              markDelinquent: mapToOptionalBoolean(data.markDelinquent),
              cureDelinquency: mapToOptionalBoolean(data.cureDelinquency),
              lateFeeAmount: data.lateFeeAmount,
              lateFeeGracePeriodDays: data.lateFeeGracePeriodDays,
              batchSize: data.batchSize,
              dryRun: mapToOptionalBoolean(data.dryRun),
            },
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'process-statement-due-date',
      toast: {
        success: 'Statement due date processed successfully',
        error: 'Failed to process statement due date',
        loading: 'Processing statement due date...',
      },
    });
  };

  const config: SmartFormConfig<z.infer<typeof schema>> = useMemo(
    () => ({
      fieldGroups: [
        {
          name: 'booleanOptions',
          label: 'Process Statement Due Date Options',
          fields: [
            {
              name: 'additionalOptions',
              label: 'Additional Options',
              type: 'boolean',
              defaultValue: false,
              as: Checkbox,
            },
          ],
        },
        {
          name: 'options',
          label: 'Additional Options',
          required: false,
          conditional: {
            when: {
              field: 'additionalOptions',
              operator: 'equals',
              value: true,
            },
            show: true,
          },
          fields: [
            {
              name: 'addInterest',
              label: 'Add Interest',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
            {
              name: 'addLateFee',
              label: 'Add Late Fee',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
            {
              name: 'markDelinquent',
              label: 'Mark Delinquent',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
            {
              name: 'cureDelinquency',
              label: 'Cure Delinquency',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
            {
              name: 'lateFeeAmount',
              label: 'Late Fee Amount',
              component: CurrencyField,
              componentProps: {
                showCurrencySymbol: true,
              },
              required: false,
            },
            {
              name: 'lateFeeGracePeriodDays',
              label: 'Late Fee Grace Period Days',
              type: 'number',
              required: false,
            },
            {
              name: 'batchSize',
              label: 'Batch Size',
              type: 'number',
              required: false,
            },
            {
              name: 'dryRun',
              label: 'Dry Run',
              type: 'select',
              options: YES_NO_OPTIONS,
              required: false,
            },
          ],
        },
      ],
      schema,
      defaultValues: {},
    }),
    [],
  );

  return createFormComponent({
    config,
    handleSubmit,
    loading,
  });
};
